import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { Product } from "@apis/index";
import { Search } from "lucide-react";
import Typography from "@components/typography";
import useConfig from "@hooks/useConfig";
import * as S from "../style";

interface ProductGridProps {
  storeId?: number;
  onAddToCart: (product: any) => void;
}

const ProductGrid: React.FC<ProductGridProps> = ({ storeId, onAddToCart }) => {
  const { bucketUrl } = useConfig();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const { items: products, loading, totalPages } = useSelector((state: RootState) => state.products);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    if (!storeId) {
      return;
    }

    const fetchProducts = async () => {
      Product.list({
        state: "products",
        params: { storeId, search: query, limit: 20, page },
      });
    };

    const debounce = setTimeout(fetchProducts, 300);
    return () => clearTimeout(debounce);
  }, [storeId, query, page]);

  if (!storeId) {
    return (
      <S.Card style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography styleText="color: #6B7280; font-size: 16px;">
          Please select a store to view products
        </Typography>
      </S.Card>
    );
  }

  return (
    <S.Card>
      <S.SearchWrapper>
        <Search size={20} color="#9CA3AF" />
        <input
          placeholder="Search products..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </S.SearchWrapper>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#6B7280" }}>
          Loading products...
        </div>
      ) : products.length > 0 ? (
        <>
          <S.ProductGrid>
            {products.map((p) => (
              <S.ProductCard key={p.id} onClick={() => onAddToCart(p)}>
                <img
                  src={p.images?.[0]?.image ? `${bucketUrl}/${p.images[0].image}` : "https://via.placeholder.com/150"}
                  alt={p.name}
                />
                <div style={{ display: "flex", flexDirection: "column", flex: 1, justifyContent: "space-between" }}>
                  <Typography styleText="font-weight: 500; font-size: 14px; line-height: 1.4;">
                    {p.name}
                  </Typography>
                  <Typography styleText="color: #10B981; font-weight: 600; margin-top: 4px;">
                    रू {p.price}
                  </Typography>
                </div>
              </S.ProductCard>
            ))}
          </S.ProductGrid>

          {totalPages > 1 && (
            <S.PaginationContainer>
              <S.PaginationButton 
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Previous
              </S.PaginationButton>
              <Typography styleText="font-weight: 500; color: #374151;">
                Page {page} of {totalPages}
              </Typography>
              <S.PaginationButton 
                disabled={page === totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
              </S.PaginationButton>
            </S.PaginationContainer>
          )}
        </>
      ) : (
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Typography styleText="color: #6B7280;">
            No products found matching your search.
          </Typography>
        </div>
      )}
    </S.Card>
  );
};


export default ProductGrid;
